import * as yup from 'yup';
import type {
  FeatureFlagDrawerMode,
  FeatureFlagFormFieldErrorKey,
  FeatureFlagFormFieldErrors,
  FeatureFlagFormValidationValues,
} from '../../../types/features/featureFlags';

const FEATURE_FLAG_KEY_PATTERN = /^[a-z0-9_]+$/;

const createFeatureFlagFormSchema = (mode: FeatureFlagDrawerMode) => yup.object({
  assignmentTarget: yup
    .mixed()
    .oneOf(['store_wise', 'user_wise']),
  description: yup.string().trim(),
  displayName: yup.string().trim().required('Feature name is required.'),
  featureKey: mode === 'create'
    ? yup
      .string()
      .trim()
      .required('Feature key is required.')
      .matches(FEATURE_FLAG_KEY_PATTERN, 'Use lowercase letters, numbers, and underscores only.')
    : yup.string().trim(),
  globalEnabled: yup.boolean().required(),
  selectedTargetClient: yup
    .mixed()
    .nullable()
    .test(
      'selected-target-client',
      'Select one target client.',
      (value) => value === 'webapp' || value === 'mobile_app',
    ),
  storeIds: yup
    .array()
    .of(yup.string().required())
    .when(['assignmentTarget', 'globalEnabled'], {
      is: (assignmentTarget: unknown, globalEnabled: unknown) => (
        assignmentTarget === 'store_wise'
        && globalEnabled !== true
      ),
      then: (schema) => schema.min(1, 'Select at least one store.'),
      otherwise: (schema) => schema,
    }),
  userIds: yup
    .array()
    .of(yup.string().required())
    .when(['assignmentTarget', 'globalEnabled'], {
      is: (assignmentTarget: unknown, globalEnabled: unknown) => (
        assignmentTarget === 'user_wise'
        && globalEnabled !== true
      ),
      then: (schema) => schema.min(1, 'Select at least one user.'),
      otherwise: (schema) => schema,
    }),
});

const mapValidationPathToField = (path?: string): FeatureFlagFormFieldErrorKey | null => {
  if (!path) {
    return null;
  }

  if (path.startsWith('storeIds')) {
    return 'storeIds';
  }

  if (path.startsWith('userIds')) {
    return 'userIds';
  }

  if (path === 'selectedTargetClient') {
    return 'targetClient';
  }

  return path as FeatureFlagFormFieldErrorKey;
};

export const validateFeatureFlagForm = async (
  values: FeatureFlagFormValidationValues,
  mode: FeatureFlagDrawerMode,
): Promise<FeatureFlagFormFieldErrors> => {
  const errors: FeatureFlagFormFieldErrors = {};

  try {
    await createFeatureFlagFormSchema(mode).validate(values, { abortEarly: false });
  } catch (error) {
    if (error instanceof yup.ValidationError) {
      const validationEntries = error.inner.length > 0 ? error.inner : [error];

      validationEntries.forEach((entry) => {
        const field = mapValidationPathToField(entry.path);

        if (field && !errors[field]) {
          errors[field] = entry.message;
        }
      });
    }
  }

  return errors;
};
